<script setup lang="ts">
import { Columns3 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { ColumnToggleOption } from '@/composables/useColumnVisibilityOverrides';

/*
 * A records page's column-visibility control: a small dropdown of checkboxes, one per toggleable
 * column that is currently on offer (already presence-gated by the caller), plus Select all/Deselect
 * all. Shared by every page that offers this control - see `useColumnVisibilityOverrides` for why the
 * underlying show/hide logic is generic while each page still owns its own column list and defaults.
 */
defineProps<{
    options: ColumnToggleOption[];
    label: string;
    /** Shows the icon alone, with the label as its accessible name, where the toolbar is short on space. */
    iconOnly?: boolean;
}>();

const emit = defineEmits<{
    toggle: [id: string];
    'select-all': [];
    'deselect-all': [];
}>();
</script>

<template>
    <DropdownMenu :modal="false">
        <DropdownMenuTrigger as-child>
            <Button variant="outline" :size="iconOnly ? 'icon' : 'sm'" class="gap-x-2" :aria-label="iconOnly ? label : undefined" :title="iconOnly ? label : undefined">
                <Columns3 class="size-4" aria-hidden="true" />
                <template v-if="!iconOnly">{{ label }}</template>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="min-w-56" align="end">
            <DropdownMenuCheckboxItem v-for="option in options" :key="option.id" :checked="option.checked" indicator-right @select.prevent="emit('toggle', option.id)">
                {{ option.label }}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem :disabled="options.every((option) => option.checked)" class="justify-center text-center" @select.prevent="emit('select-all')">
                {{ $t('global.uiElements.multiSelect.selectAllButtonLabel') }}
            </DropdownMenuItem>
            <DropdownMenuItem :disabled="options.every((option) => !option.checked)" class="justify-center text-center" @select.prevent="emit('deselect-all')">
                {{ $t('global.uiElements.multiSelect.deselectAllButtonLabel') }}
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
